import { Device } from "@app/shared/src/models";
import { siteLabel } from "./deviceLabels";

/**
 * Which trucks a director should actually chase, and why.
 *
 * One definition, used by both the Overview card and the Devices page filter, so the list you
 * click through from can never disagree with the list you land on.
 *
 * Deliberately NOT "anything not reporting right now". A TMA is powered only while it is deployed,
 * so a yard full of parked trucks is the normal overnight state of a fleet; flagging each one buries
 * the handful that are genuinely broken. What is left is a unit that has been silent long enough
 * that someone should go and look at it, and a unit drifting behind the rest on firmware.
 */

/** Silent this long and it is no longer "parked", it is unaccounted for. */
export const SILENT_MS = 14 * 24 * 60 * 60 * 1000;

export type AttentionReason = "silent" | "firmware";

export type DeviceAttention = {
    device: Device;
    reason: AttentionReason;
    tone: "crit" | "warn";
    title: string;
    sub: string;
};

/** The newest firmware anywhere in the fleet — what "behind" is measured against. */
export function newestFirmware(devices: Device[]): string | undefined {
    const versions = devices.map((d) => d.firmwareVersion).filter(Boolean) as string[];
    if (versions.length === 0) return undefined;
    return [...versions].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).pop();
}

export function silentSince(device: Device, now = Date.now()): number {
    return now - new Date(device.lastHeartbeat ?? 0).getTime();
}

/**
 * The attention list, worst first. A unit that is both silent and behind appears once, under the
 * more serious reason — two rows for one truck reads as two problems.
 */
export function deviceAttention(devices: Device[], now = Date.now()): DeviceAttention[] {
    const newest = newestFirmware(devices);

    const silent = devices.filter((d) => silentSince(d, now) > SILENT_MS);
    const silentIds = new Set(silent.map((d) => d.id));
    const behind = newest ? devices.filter((d) => d.firmwareVersion && d.firmwareVersion !== newest && !silentIds.has(d.id)) : [];

    // Headed by the site, keyed by the device: the TMA number is what a director recognises, and
    // the key underneath is what they read off the unit when they go and find it.
    const key = (device: Device) => `Device key: ${device.deviceKey || "—"}`;

    return [
        ...silent.map(
            (device): DeviceAttention => ({
                device,
                reason: "silent",
                tone: "crit",
                title: `${siteLabel(device)} not seen in 14+ days`,
                sub: key(device),
            }),
        ),
        ...behind.map(
            (device): DeviceAttention => ({
                device,
                reason: "firmware",
                tone: "warn",
                title: `${siteLabel(device)} behind firmware ${newest}`,
                sub: `${key(device)} · running ${device.firmwareVersion}`,
            }),
        ),
    ];
}

/** The ids on the attention list — what the Devices page filters rows against. */
export function attentionIds(devices: Device[], now = Date.now()): Set<string | undefined> {
    return new Set(deviceAttention(devices, now).map((a) => a.device.id));
}
